import { MountainAssistantCore } from "../core/MountainAssistantCore.js";
import { MountainAdviceRules } from "../advice/MountainAdviceRules.js";
import { ResponseBuilder } from "../core/ResponseBuilder.js";
import type { AssistantResponse } from "../types/AssistantResponse.js";
import type { MountainContext } from "../types/MountainContext.js";
import type { MountainCommand } from "./MountainCommand.js";

const km = (meters: number): string => (meters / 1000).toFixed(meters < 1000 ? 2 : 1).replace(".0", "");
const fixed = (value: number, digits = 5): string => value.toFixed(digits);

const unavailable = (context: MountainContext, field: string): AssistantResponse =>
  ResponseBuilder.build({
    message: "Informacion no disponible.",
    severity: "INSUFFICIENT_DATA",
    confidence: 1,
    dataUsed: [],
    dataTimestamp: context.timestamp,
    offline: context.offline,
    recommendedAction: `Obtenga datos validos de ${field} antes de repetir el comando.`,
    limitations: [`No hay datos disponibles de ${field}.`],
    source: "COMMAND_ENGINE",
  });

export class CommandExecutor {
  constructor(
    private readonly core: MountainAssistantCore = new MountainAssistantCore(),
    private readonly adviceRules: MountainAdviceRules = new MountainAdviceRules(),
  ) {}

  async execute(command: MountainCommand, context: MountainContext): Promise<AssistantResponse> {
    const adviceResponse = this.adviceRules.evaluate(command, context);
    if (adviceResponse) return adviceResponse;

    switch (command) {
      case "DISTANCE_REMAINING":
        if (!context.route) return unavailable(context, "ruta");
        return ResponseBuilder.build({
          message: `Te faltan aproximadamente ${km(context.route.distanceRemainingMeters)} km.`,
          severity: context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
          confidence: 0.95,
          dataUsed: ["route.distanceRemainingMeters"],
          dataTimestamp: context.route.timestamp,
          offline: context.offline,
          recommendedAction: "Usa la distancia como referencia y confirma tu posicion en la ruta.",
          limitations: context.offline ? ["Funcionamiento offline limitado."] : [],
          source: "COMMAND_ENGINE",
        });

      case "ROUTE_PROGRESS":
        if (!context.route) return unavailable(context, "ruta");
        return ResponseBuilder.build({
          message: `Has completado aproximadamente el ${Math.round(context.route.progressPercent)} % de la ruta.`,
          severity: context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
          confidence: 0.95,
          dataUsed: ["route.progressPercent"],
          dataTimestamp: context.route.timestamp,
          offline: context.offline,
          recommendedAction: "Usa el progreso como referencia, no como garantia de llegada segura.",
          limitations: context.offline ? ["Funcionamiento offline limitado."] : [],
          source: "COMMAND_ENGINE",
        });

      case "DISTANCE_TRAVELLED":
        if (!context.route) return unavailable(context, "ruta");
        return ResponseBuilder.build({
          message: `Has recorrido aproximadamente ${km(context.route.distanceTravelledMeters)} km.`,
          severity: context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
          confidence: 0.95,
          dataUsed: ["route.distanceTravelledMeters"],
          dataTimestamp: context.route.timestamp,
          offline: context.offline,
          recommendedAction: "Usa este valor como referencia del recorrido registrado.",
          limitations: [],
          source: "COMMAND_ENGINE",
        });

      case "ROUTE_DEVIATION":
        if (!context.route || context.route.routeDeviationMeters === null) return unavailable(context, "desviacion de ruta");
        return ResponseBuilder.build({
          message: context.route.routeDeviationMeters < 30
            ? `Estas cerca del trazado registrado; desviacion aproximada: ${Math.round(context.route.routeDeviationMeters)} m.`
            : `Estas aproximadamente a ${Math.round(context.route.routeDeviationMeters)} m del trazado registrado.`,
          severity: context.route.routeDeviationMeters >= 150 ? "WARNING" : context.route.routeDeviationMeters >= 30 ? "CAUTION" : "INFORMATION",
          confidence: 0.9,
          dataUsed: ["route.routeDeviationMeters"],
          dataTimestamp: context.route.timestamp,
          offline: context.offline,
          recommendedAction: context.route.routeDeviationMeters >= 30 ? "Detente y verifica tu posicion antes de continuar." : "Continua verificando periodicamente tu posicion.",
          limitations: ["Los umbrales de desviacion de Beta V1 son experimentales."],
          source: "COMMAND_ENGINE",
        });

      case "CURRENT_ALTITUDE":
        if (!context.gps || context.gps.altitudeMeters === null) return unavailable(context, "altitud");
        return ResponseBuilder.build({
          message: `Altitud registrada: aproximadamente ${Math.round(context.gps.altitudeMeters)} m s. n. m.`,
          severity: context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
          confidence: 0.9,
          dataUsed: ["gps.altitudeMeters"],
          dataTimestamp: context.gps.timestamp,
          offline: context.offline,
          recommendedAction: "Interpreta la altitud junto con la precision y condiciones reales del dispositivo.",
          limitations: ["La altitud depende de la calidad del dato recibido."],
          source: "COMMAND_ENGINE",
        });

      case "GPS_ACCURACY":
        if (!context.gps) return unavailable(context, "GPS");
        return ResponseBuilder.build({
          message: `Precision GPS reportada: aproximadamente +/-${Math.round(context.gps.accuracyMeters)} m.`,
          severity: context.gps.accuracyMeters > 50 ? "CAUTION" : "INFORMATION",
          confidence: 1,
          dataUsed: ["gps.accuracyMeters"],
          dataTimestamp: context.gps.timestamp,
          offline: context.offline,
          recommendedAction: context.gps.accuracyMeters > 50 ? "Espera una mejor precision antes de tomar decisiones finas de navegacion." : "Mantente atento a cambios en la precision GPS.",
          limitations: ["El umbral de 50 m es experimental en Beta V1."],
          source: "COMMAND_ENGINE",
        });

      case "GPS_STATUS":
        if (!context.gps) return unavailable(context, "GPS");
        return ResponseBuilder.build({
          message: context.gps.accuracyMeters <= 20 ? "El GPS presenta una precision util para esta prueba beta." : context.gps.accuracyMeters <= 50 ? "El GPS presenta precision limitada." : "La precision GPS es insuficiente para una orientacion fiable.",
          severity: context.gps.accuracyMeters > 50 ? "CAUTION" : "INFORMATION",
          confidence: 0.9,
          dataUsed: ["gps.accuracyMeters"],
          dataTimestamp: context.gps.timestamp,
          offline: context.offline,
          recommendedAction: context.gps.accuracyMeters > 50 ? "Detente y espera una mejor precision." : "Sigue verificando la precision durante el recorrido.",
          limitations: ["Clasificacion experimental para Beta V1."],
          source: "COMMAND_ENGINE",
        });

      case "OFFLINE_STATUS":
        return ResponseBuilder.build({
          message: context.offline ? "Estas en modo offline limitado." : "El contexto indica funcionamiento online.",
          severity: context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
          confidence: 1,
          dataUsed: ["offline"],
          dataTimestamp: context.timestamp,
          offline: context.offline,
          recommendedAction: context.offline ? "Usa solo los datos locales disponibles y considera que algunos datos pueden no actualizarse." : "Continua verificando la vigencia de los datos recibidos.",
          limitations: context.offline ? ["No se asume disponibilidad de servicios remotos."] : [],
          source: "COMMAND_ENGINE",
        });

      case "AVAILABLE_DATA": {
        const available = [context.gps ? "GPS" : null, context.route ? "ruta" : null, context.weather ? "clima" : null, context.alerts !== undefined ? "alertas" : null].filter((value): value is string => value !== null);
        return ResponseBuilder.build({
          message: available.length ? `Datos disponibles: ${available.join(", ")}.` : "Informacion no disponible: no hay datos contextuales cargados.",
          severity: available.length ? (context.offline ? "OFFLINE_LIMITED" : "INFORMATION") : "INSUFFICIENT_DATA",
          confidence: 1,
          dataUsed: available.map((value) => value.toLowerCase()),
          dataTimestamp: context.timestamp,
          offline: context.offline,
          recommendedAction: available.length ? "Consulta solo los datos indicados como disponibles." : "Carga datos contextuales antes de continuar.",
          limitations: context.offline ? ["Algunos datos pueden estar desactualizados en modo offline."] : [],
          source: "COMMAND_ENGINE",
        });
      }

      case "ALERT_STATUS": {
        const alerts = context.alerts ?? [];
        const critical = alerts.find((alert) => alert.level === "CRITICAL");
        const warning = alerts.find((alert) => alert.level === "WARNING");
        const caution = alerts.find((alert) => alert.level === "CAUTION");
        const strongest = critical ?? warning ?? caution;
        return ResponseBuilder.build({
          message: strongest ? `Alerta activa: ${strongest.message}` : "No hay alertas activas en los datos recibidos.",
          severity: strongest?.level === "CRITICAL" ? "CRITICAL" : strongest?.level === "WARNING" ? "WARNING" : strongest?.level === "CAUTION" ? "CAUTION" : "INFORMATION",
          confidence: 1,
          dataUsed: ["alerts"],
          dataTimestamp: strongest?.timestamp ?? context.timestamp,
          offline: context.offline,
          recommendedAction: strongest ? "Revisa la alerta antes de continuar y prioriza una decision conservadora." : "Continua atento a nuevas alertas y a las condiciones reales.",
          limitations: ["La ausencia de alertas no garantiza que la ruta sea segura."],
          source: "COMMAND_ENGINE",
        });
      }

      case "CONTINUE_ASSESSMENT": {
        const result = await this.core.evaluate(context);
        if (result.severity === "CRITICAL" || result.severity === "WARNING" || result.severity === "CAUTION") return result;
        return ResponseBuilder.build({
          message: "No se detectaron alertas deterministas con los datos disponibles, pero Mountain AI no puede confirmar que sea seguro continuar.",
          severity: context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
          confidence: 0.8,
          dataUsed: result.data_used,
          dataTimestamp: result.data_timestamp,
          offline: context.offline,
          recommendedAction: "Evalua el terreno, clima, estado fisico y condiciones reales antes de decidir continuar.",
          limitations: ["Mountain AI no sustituye la decision del montanista ni garantiza seguridad."],
          source: "COMMAND_ENGINE",
        });
      }

      case "BASIC_RECOMMENDATION": {
        const result = await this.core.evaluate(context);
        if (result.severity === "CRITICAL" || result.severity === "WARNING" || result.severity === "CAUTION") return result;
        return ResponseBuilder.build({
          message: "Recomendacion basica: manten control de tu posicion, progreso y condiciones reales antes de continuar.",
          severity: context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
          confidence: 0.75,
          dataUsed: result.data_used,
          dataTimestamp: result.data_timestamp,
          offline: context.offline,
          recommendedAction: "Verifica ruta, GPS, clima disponible y tu estado antes de tomar decisiones.",
          limitations: ["Recomendacion general; no sustituye criterio profesional ni evaluacion del terreno."],
          source: "COMMAND_ENGINE",
        });
      }

      case "CURRENT_POSITION":
        if (!context.gps) return unavailable(context, "GPS");
        return ResponseBuilder.build({
          message: `Posicion registrada: ${fixed(context.gps.latitude)}, ${fixed(context.gps.longitude)}.`,
          severity: context.gps.accuracyMeters > 50 ? "CAUTION" : "INFORMATION",
          confidence: context.gps.accuracyMeters > 50 ? 0.7 : 0.95,
          dataUsed: ["gps.latitude", "gps.longitude", "gps.accuracyMeters"],
          dataTimestamp: context.gps.timestamp,
          offline: context.offline,
          recommendedAction: "Considera la precision GPS antes de usar estas coordenadas para navegacion.",
          limitations: [`Precision reportada: +/-${Math.round(context.gps.accuracyMeters)} m.`],
          source: "COMMAND_ENGINE",
        });

      case "TIME_REMAINING":
        if (!context.route || context.route.estimatedTimeRemainingMinutes === null) return unavailable(context, "tiempo restante");
        return ResponseBuilder.build({
          message: `Tiempo restante estimado: aproximadamente ${Math.round(context.route.estimatedTimeRemainingMinutes)} minutos.`,
          severity: context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
          confidence: 0.8,
          dataUsed: ["route.estimatedTimeRemainingMinutes"],
          dataTimestamp: context.route.timestamp,
          offline: context.offline,
          recommendedAction: "Usa esta estimacion solo como referencia; el terreno y las condiciones pueden cambiar el tiempo real.",
          limitations: ["Es una estimacion y no una hora garantizada de llegada."],
          source: "COMMAND_ENGINE",
        });

      case "ROUTE_STATUS":
        if (!context.route) return unavailable(context, "ruta");
        return ResponseBuilder.build({
          message: `Ruta: ${Math.round(context.route.progressPercent)} % completado, ${km(context.route.distanceRemainingMeters)} km restantes${context.route.routeDeviationMeters === null ? "" : `, desviacion aproximada ${Math.round(context.route.routeDeviationMeters)} m`}.`,
          severity: context.route.routeDeviationMeters !== null && context.route.routeDeviationMeters >= 150 ? "WARNING" : context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
          confidence: 0.9,
          dataUsed: ["route.progressPercent", "route.distanceRemainingMeters", "route.routeDeviationMeters"],
          dataTimestamp: context.route.timestamp,
          offline: context.offline,
          recommendedAction: "Usa el resumen para orientarte y confirma las condiciones reales antes de decidir.",
          limitations: ["Los umbrales de desviacion de Beta V1 son experimentales."],
          source: "COMMAND_ENGINE",
        });
    }

    return ResponseBuilder.build({
      message: "Comando no disponible en esta version.",
      severity: "INSUFFICIENT_DATA",
      confidence: 1,
      dataUsed: [],
      dataTimestamp: context.timestamp,
      offline: context.offline,
      recommendedAction: "Usa uno de los comandos documentados para Beta V1.2.",
      limitations: ["El sistema no inventa una respuesta para comandos no implementados."],
      source: "COMMAND_ENGINE",
    });
  }
}
